import { useMemo, useState } from "react";

import { FolderKey, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { EndpointDef, PermissionGroupDef } from "@/features/users/data/endpoints";
import { useEndpointsConfig } from "@/features/users/hooks/useEndpointsConfig";
import {
  useCreatePermissionGroup,
  useDeletePermissionGroup,
  usePermissionGroups,
  useUpdatePermissionGroup,
} from "@/features/users/hooks/usePermissionGroups";
import { slugifyGroupId } from "@/features/users/utils/slugifyGroupId";
import { cn } from "@/shared/lib/utils";

interface PermissionGroupsDialogProps {
  open: boolean;
  onClose: () => void;
}

type EditorMode = "idle" | "create" | "edit";

function groupEndpointsByCategory(
  endpoints: EndpointDef[]
): Record<string, EndpointDef[]> {
  const groups: Record<string, EndpointDef[]> = {};
  for (const ep of endpoints) {
    if (!groups[ep.category]) groups[ep.category] = [];
    groups[ep.category].push(ep);
  }
  return groups;
}

export function PermissionGroupsDialog({ open, onClose }: PermissionGroupsDialogProps) {
  const { data: endpoints = [], isLoading: isLoadingEndpoints } = useEndpointsConfig();
  const { data: groups = [], isLoading: isLoadingGroups } = usePermissionGroups();
  const createGroup = useCreatePermissionGroup();
  const updateGroup = useUpdatePermissionGroup();
  const deleteGroup = useDeletePermissionGroup();

  const [mode, setMode] = useState<EditorMode>("idle");
  const [editingGroup, setEditingGroup] = useState<PermissionGroupDef | null>(null);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [permQuery, setPermQuery] = useState("");

  const endpointsByDocId = useMemo(() => {
    const map = new Map<string, EndpointDef>();
    for (const ep of endpoints) map.set(ep.docId, ep);
    return map;
  }, [endpoints]);

  const filteredByCategory = useMemo(() => {
    const q = permQuery.trim().toLowerCase();
    const matches = endpoints.filter((ep) => {
      if (!q) return true;
      return (
        ep.label.toLowerCase().includes(q) ||
        ep.id.toLowerCase().includes(q) ||
        ep.category.toLowerCase().includes(q)
      );
    });
    return groupEndpointsByCategory(matches);
  }, [endpoints, permQuery]);

  const selectedSet = useMemo(() => new Set(selectedDocIds), [selectedDocIds]);
  const isSaving = createGroup.isPending || updateGroup.isPending;

  const resetEditor = () => {
    setMode("idle");
    setEditingGroup(null);
    setLabel("");
    setDescription("");
    setSelectedDocIds([]);
    setPermQuery("");
  };

  const startCreate = () => {
    setMode("create");
    setEditingGroup(null);
    setLabel("");
    setDescription("");
    setSelectedDocIds([]);
    setPermQuery("");
  };

  const startEdit = (group: PermissionGroupDef) => {
    setMode("edit");
    setEditingGroup(group);
    setLabel(group.label);
    setDescription(group.description ?? "");
    setSelectedDocIds([...group.permissionIds]);
    setPermQuery("");
  };

  const toggleDocId = (docId: string) => {
    setSelectedDocIds((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const toggleCategory = (categoryEndpoints: EndpointDef[]) => {
    const ids = categoryEndpoints.map((ep) => ep.docId);
    const allSelected = ids.every((id) => selectedSet.has(id));
    if (allSelected) {
      const remove = new Set(ids);
      setSelectedDocIds((prev) => prev.filter((id) => !remove.has(id)));
    } else {
      setSelectedDocIds((prev) => Array.from(new Set([...prev, ...ids])));
    }
  };

  const handleSave = async () => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      toast.error("El nombre del grupo es obligatorio");
      return;
    }
    if (selectedDocIds.length === 0) {
      toast.error("Seleccioná al menos un permiso");
      return;
    }

    const slug = slugifyGroupId(trimmedLabel);

    try {
      if (mode === "create") {
        await createGroup.mutateAsync({
          id: slug,
          label: trimmedLabel,
          description: description.trim() || undefined,
          permissionIds: selectedDocIds,
        });
        toast.success(`Grupo "${trimmedLabel}" creado`);
      } else if (mode === "edit" && editingGroup) {
        await updateGroup.mutateAsync({
          docId: editingGroup.docId,
          data: {
            id: slug,
            label: trimmedLabel,
            description: description.trim() || undefined,
            permissionIds: selectedDocIds,
          },
        });
        toast.success(`Grupo "${trimmedLabel}" actualizado`);
      }
      resetEditor();
    } catch (error) {
      console.error("Error guardando grupo:", error);
      toast.error(
        error instanceof Error ? error.message : "No se pudo guardar el grupo"
      );
    }
  };

  const handleDelete = async (group: PermissionGroupDef) => {
    const ok = window.confirm(
      `¿Eliminar el grupo "${group.label}"? No afecta API keys ya creadas.`
    );
    if (!ok) return;

    try {
      await deleteGroup.mutateAsync(group.docId);
      toast.success(`Grupo "${group.label}" eliminado`);
      if (editingGroup?.docId === group.docId) resetEditor();
    } catch (error) {
      console.error("Error eliminando grupo:", error);
      toast.error(
        error instanceof Error ? error.message : "No se pudo eliminar el grupo"
      );
    }
  };

  const isLoading = isLoadingEndpoints || isLoadingGroups;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FolderKey className="h-6 w-6 text-primary" />
            Grupos de permisos
          </DialogTitle>
          <DialogDescription>
            Definí presets (ej. Router, Bridge). Al crear una API Key podés aplicar un
            grupo o elegir endpoints uno a uno.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {mode === "idle" && (
            <>
              <div className="flex justify-end">
                <Button onClick={startCreate} disabled={isLoading}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo grupo
                </Button>
              </div>

              {isLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : groups.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">
                  No hay grupos todavía. Creá uno (por ejemplo &quot;Router&quot;) y
                  asignale permisos.
                </p>
              ) : (
                <div className="space-y-2">
                  {groups.map((group) => (
                    <div
                      key={group.docId}
                      className="flex items-start justify-between gap-3 rounded-lg border p-3"
                    >
                      <div className="min-w-0 space-y-1">
                        <p className="font-medium">{group.label}</p>
                        {group.description && (
                          <p className="text-xs text-muted-foreground">
                            {group.description}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {group.permissionIds.length} permiso(s) · id: {group.id}
                        </p>
                        <div className="flex flex-wrap gap-1 pt-1">
                          {group.permissionIds.slice(0, 6).map((docId) => {
                            const ep = endpointsByDocId.get(docId);
                            return (
                              <span
                                key={docId}
                                className="rounded bg-muted px-1.5 py-0.5 text-[10px]"
                                title={ep?.id ?? docId}
                              >
                                {ep?.label ?? docId.slice(0, 8)}
                              </span>
                            );
                          })}
                          {group.permissionIds.length > 6 && (
                            <span className="text-[10px] text-muted-foreground">
                              +{group.permissionIds.length - 6}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Editar"
                          onClick={() => startEdit(group)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Eliminar"
                          onClick={() => handleDelete(group)}
                          disabled={deleteGroup.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {(mode === "create" || mode === "edit") && (
            <div className="space-y-4 rounded-lg border bg-muted/20 p-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">
                  {mode === "create" ? "Nuevo grupo" : `Editar: ${editingGroup?.label}`}
                </Label>
                <Button variant="ghost" size="icon" onClick={resetEditor}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="group-label">Nombre</Label>
                  <Input
                    id="group-label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Ej: Router"
                  />
                  {label.trim() && (
                    <p className="text-[11px] text-muted-foreground">
                      id: {slugifyGroupId(label)}
                    </p>
                  )}
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="group-desc">Descripción (opcional)</Label>
                  <Textarea
                    id="group-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Desktop: SSE worklist + upload"
                    rows={2}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Permisos del grupo</Label>
                  <span className="text-xs text-muted-foreground">
                    {selectedDocIds.length} seleccionado(s)
                  </span>
                </div>
                <Input
                  value={permQuery}
                  onChange={(e) => setPermQuery(e.target.value)}
                  placeholder="Buscar permiso…"
                />

                {isLoadingEndpoints ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="max-h-64 space-y-3 overflow-y-auto rounded-md border p-2">
                    {Object.keys(filteredByCategory).length === 0 ? (
                      <p className="py-4 text-center text-xs text-muted-foreground">
                        No hay permisos en api_permissions o no coinciden con la búsqueda.
                      </p>
                    ) : (
                      Object.entries(filteredByCategory).map(
                        ([category, categoryEndpoints]) => {
                          const ids = categoryEndpoints.map((ep) => ep.docId);
                          const allSelected = ids.every((id) => selectedSet.has(id));
                          return (
                            <div key={category}>
                              <div className="mb-1 flex items-center justify-between px-1">
                                <span className="text-xs font-medium text-muted-foreground">
                                  {category}
                                </span>
                                <button
                                  type="button"
                                  className="text-[10px] text-muted-foreground hover:text-foreground"
                                  onClick={() => toggleCategory(categoryEndpoints)}
                                >
                                  {allSelected ? "Quitar todos" : "Seleccionar todos"}
                                </button>
                              </div>
                              <div className="space-y-0.5">
                                {categoryEndpoints.map((ep) => {
                                  const checked = selectedSet.has(ep.docId);
                                  return (
                                    <label
                                      key={ep.docId}
                                      className={cn(
                                        "flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50",
                                        checked && "bg-accent/30"
                                      )}
                                    >
                                      <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={checked}
                                        onChange={() => toggleDocId(ep.docId)}
                                      />
                                      <span className="min-w-0 flex-1">
                                        <span className="block font-medium leading-tight">
                                          {ep.label}
                                        </span>
                                        <span className="block truncate font-mono text-[10px] text-muted-foreground">
                                          {ep.method} {ep.id}
                                        </span>
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                      )
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {mode === "create" ? "Crear grupo" : "Guardar cambios"}
                </Button>
                <Button variant="outline" onClick={resetEditor} disabled={isSaving}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
